import { signEvent, checkEvent } from './event.js';
import { normalizePubkey } from './keys.js';
import { KIND } from './kinds.js';
import { tagValue, now } from './util.js';

/**
 * Sender registry — an optional gate on top of a permissionless protocol.
 *
 * Herald itself is open: anyone can generate a key and publish a channel, and
 * that is the right default for a protocol. A *product* usually wants the
 * opposite — only vetted companies should be able to reach its users — so the
 * gate lives in relay policy, not in the wire format.
 *
 * Admissions are signed events by an operator key rather than rows in a
 * database, for the same reason consent is: a subscriber can then verify
 * "this sender was admitted by the operator whose key I trust", and an
 * operator cannot quietly rewrite history.
 *
 * A relay in `open` mode ignores all of this.
 */

export const REGISTRY_STATUS = ['admitted', 'suspended', 'rejected'];

/**
 * @param {{sender:string, status:string, name?:string, domain?:string,
 *          note?:string, reviewedAt?:number}} entry
 * @param {Uint8Array|string} operatorSecretKey
 */
export function buildRegistryEntry(entry, operatorSecretKey) {
  const sender = normalizePubkey(entry.sender);
  if (!REGISTRY_STATUS.includes(entry.status)) {
    throw new Error(`registry status must be one of ${REGISTRY_STATUS.join(', ')}`);
  }
  const content = JSON.stringify({
    status: entry.status,
    name: entry.name ?? null,
    domain: entry.domain ?? null,
    note: entry.note ?? '',
    reviewed_at: entry.reviewedAt ?? now(),
  });
  return signEvent({
    kind: KIND.REGISTRY_ENTRY,
    content,
    // `d` is the sender root, so the entry is addressable and the latest
    // decision for a sender replaces the previous one.
    tags: [['d', sender], ['p', sender], ['status', entry.status]],
    created_at: entry.createdAt,
  }, operatorSecretKey);
}

export function parseRegistryEntry(event) {
  if (event.kind !== KIND.REGISTRY_ENTRY) throw new Error(`not a registry entry (kind ${event.kind})`);
  const sender = tagValue(event, 'd');
  if (!sender) throw new Error('registry entry missing d tag');
  const body = JSON.parse(event.content);
  return {
    operator: event.pubkey,
    sender,
    status: body.status,
    name: body.name ?? null,
    domain: body.domain ?? null,
    note: body.note ?? '',
    reviewedAt: body.reviewed_at ?? event.created_at,
    updatedAt: event.created_at,
  };
}

/**
 * Verify an admission against the operator key a client chose to trust.
 * @returns {{ok:true, entry:object}|{ok:false, error:string}}
 */
export function verifyRegistryEntry(event, operatorPubkey) {
  const structural = checkEvent(event);
  if (!structural.ok) return { ok: false, error: `registry entry invalid: ${structural.error}` };
  if (event.pubkey !== normalizePubkey(operatorPubkey)) {
    return { ok: false, error: 'registry entry is not signed by the trusted operator' };
  }
  try {
    return { ok: true, entry: parseRegistryEntry(event) };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}
